import { extractErrorProperties, normalizeToError } from '@/lib/errors';
import { isPostHogEnabled, posthog } from '@/posthog';

type LogProperties = Record<string, unknown>;

const LOGGER_PREFIX_DTC = 'dtc__intake__';
const LOGGER_PREFIX_PRODUCT_SELECTION = `${LOGGER_PREFIX_DTC}product_selection__`;
const LOGGER_PREFIX_TELEHEALTH_INTRO = `${LOGGER_PREFIX_DTC}telehealth_intro__`;
const LOGGER_PREFIX_INSURANCE_CARD_UPLOAD = `${LOGGER_PREFIX_DTC}insurance_card_upload__`;
const LOGGER_PREFIX_ORDER_LOOKUP = `${LOGGER_PREFIX_DTC}order_lookup__`;

export const LOGGER_ACTIONS = {
  INTAKE: {
    INSURANCE_CARD_UPLOAD_COMPLETED: `${LOGGER_PREFIX_INSURANCE_CARD_UPLOAD}completed`,
    INSURANCE_CARD_UPLOAD_SKIPPED: `${LOGGER_PREFIX_INSURANCE_CARD_UPLOAD}skipped`,
    INSURANCE_CARD_UPLOAD_SUBMITTED: `${LOGGER_PREFIX_INSURANCE_CARD_UPLOAD}submitted`,
    PRODUCT_SELECTION_SKIPPED: `${LOGGER_PREFIX_PRODUCT_SELECTION}skipped`,
    NO_PRODUCTS_SELECTED: `${LOGGER_PREFIX_PRODUCT_SELECTION}no_products_selected`,
    REQUEST_COVERED_SUPPLIES: `${LOGGER_PREFIX_PRODUCT_SELECTION}request_covered_supplies`,
    EXISTING_RX_CLICKED: `${LOGGER_PREFIX_TELEHEALTH_INTRO}existing_rx_clicked`,
    EXISTING_RX_CALL_CLICKED: `${LOGGER_PREFIX_TELEHEALTH_INTRO}existing_rx_call_clicked`,
  },
  ORDER_LOOKUP: {
    BANNER_CLICKED: `${LOGGER_PREFIX_ORDER_LOOKUP}banner_clicked`,
    IDENTITY_SUBMITTED: `${LOGGER_PREFIX_ORDER_LOOKUP}identity_submitted`,
    OTP_SENT: `${LOGGER_PREFIX_ORDER_LOOKUP}otp_sent`,
    OTP_RESENT: `${LOGGER_PREFIX_ORDER_LOOKUP}otp_resent`,
    OTP_VERIFIED: `${LOGGER_PREFIX_ORDER_LOOKUP}otp_verified`,
    OTP_FAILED: `${LOGGER_PREFIX_ORDER_LOOKUP}otp_failed`,
    START_FAILED: `${LOGGER_PREFIX_ORDER_LOOKUP}start_failed`,
    HANDOFF: `${LOGGER_PREFIX_ORDER_LOOKUP}handoff`,
    CONTACT_SUPPORT_CLICKED: `${LOGGER_PREFIX_ORDER_LOOKUP}contact_support_clicked`,
  },
};

/**
 * Logger tightly coupled with PostHog for tracking events and errors.
 * Falls back to console methods if PostHog is not enabled.
 */
const logger = {
  /**
   * Log an error to PostHog with automatic property extraction.
   *
   * Automatically extracts properties based on error type:
   * - ApiError: status, statusText, url, responseData
   * - ZodError: validation issues
   * - Error: standard error properties
   *
   * @example
   * // With custom message (becomes PostHog title)
   * logger.error('Failed to create order', error, { orderId: '123' });
   *
   * @example
   * // Without message (uses error.message)
   * logger.error(error, { action: 'resend_verification' });
   *
   * @example
   * // Minimal
   * logger.error(error);
   */
  error(
    messageOrError: string | Error | unknown,
    errorOrProps?: Error | unknown | LogProperties,
    props?: LogProperties
  ) {
    let message: string | undefined;
    let originalError: unknown;
    let additionalProps: LogProperties = {};

    // Parse arguments based on what was passed
    if (typeof messageOrError === 'string') {
      // Signature: error(message, error, props?)
      message = messageOrError;
      originalError = errorOrProps;
      additionalProps = props ?? {};
    } else {
      // Signature: error(error, props?)
      originalError = messageOrError;
      additionalProps = (errorOrProps as LogProperties) ?? {};
    }

    // Auto-extract properties from error (ApiError, ZodError, etc.)
    const errorProps = extractErrorProperties(originalError);
    const allProps = { ...errorProps, ...additionalProps };

    // Create wrapper with custom message, or normalize original
    const errorToCapture = message
      ? new Error(message, { cause: originalError })
      : normalizeToError(originalError);

    if (isPostHogEnabled) {
      posthog.captureException(errorToCapture, allProps);
    }

    if (import.meta.env.MODE === 'development') {
      console.error(errorToCapture, allProps);
    } else {
      console.error(errorToCapture);
    }
  },

  // TODO: Add logger info and warn for posthog (it's in alpha)

  /**
   * Log an action event to PostHog (for tracking user actions)
   * @param action - Action name or event name
   * @param properties - Additional properties to include with the event
   */
  action(action: string, properties?: LogProperties) {
    if (isPostHogEnabled) {
      posthog.capture(action, properties);
    }
  },
};

export { logger };
